import type { ReactNode } from "react";

import {
  Field as ChakraField,
  Input,
  InputGroup,
  type InputProps,
  Stack,
} from "@chakra-ui/react";

import { Badge } from "../../data-display/badge";

type Props = {
  actionEl?: ReactNode;
  actionOffset?: number;
  additionalSlot?: ReactNode;
  badgeText?: string;
  children?: ReactNode;
  disabled?: boolean;
  errorText?: string;
  helperText?: string;
  input: InputProps;
  inputBgColor?: string;
  inputBorderColor?: string;
  label?: string;
  labelColor?: string;
  labelFontSize?: string;
  labelWeight?: string;
  leftEl?: ReactNode;
  placeholder?: string;
  rightEl?: ReactNode;
  isInvalid: boolean;
  isRequired?: boolean;
};

export const InputField = ({
  actionEl,
  actionOffset,
  additionalSlot,
  badgeText,
  children,
  disabled,
  errorText,
  helperText,
  input,
  inputBgColor = "white",
  inputBorderColor = "bg.subtle",
  label,
  labelColor,
  labelFontSize = "sm",
  labelWeight,
  leftEl,
  placeholder,
  rightEl,
  isInvalid,
  isRequired,
}: Props) => {
  return (
    <ChakraField.Root
      disabled={disabled}
      invalid={isInvalid}
      required={isRequired}
    >
      {label ? (
        <ChakraField.Label
          css={{
            color: isInvalid ? "red.500" : labelColor,
            fontSize: labelFontSize,
            fontWeight: labelWeight,
          }}
        >
          {label} <ChakraField.RequiredIndicator />
          {badgeText ? <Badge mx={1}>{badgeText}</Badge> : null}
        </ChakraField.Label>
      ) : null}
      <InputGroup endAddon={rightEl} endElement={actionEl} startAddon={leftEl}>
        <Stack direction="row" gap="8px" w="full">
          {additionalSlot}
          <Input
            _focus={{
              borderColor: "fg.accent",
            }}
            focusVisibleRing={"none"}
            placeholder={placeholder}
            pr={actionOffset ? `${actionOffset}px !important` : undefined}
            {...input}
            css={{
              backgroundColor: isInvalid ? "red.100" : inputBgColor,
              border: "1px solid",
              borderColor: isInvalid ? "red.500" : inputBorderColor,
              transition: "all 0.2s ease",
              width: "100%",
            }}
          />
        </Stack>
      </InputGroup>
      {isInvalid && errorText && (
        <ChakraField.ErrorText>{errorText} &nbsp;</ChakraField.ErrorText>
      )}
      {!isInvalid && helperText && (
        <ChakraField.HelperText>{helperText} &nbsp;</ChakraField.HelperText>
      )}
      {children}
    </ChakraField.Root>
  );
};
